/**
 * Temporary screen-scoped design tokens for **Settings → Branding**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Branding" section) 1:1.
 * Every colour, size, radius, spacing and type value the screen needs lives
 * here — components must never inline a literal. Once all screens are
 * re-skinned the shared values move into `src/theme` and this file is removed.
 */

import {
  button,
  contentCard,
  formField,
  notification,
  signer,
  switchControl,
  white,
} from "theme/colors";

export const brandingTokens = {
  /** Settings column + sticky preview panel side by side, wrapping when narrow. */
  layout: {
    gap: "20px",
    /** `flex: 1 1 480px` — the stack of white setting cards. */
    settingsBasis: "480px",
    /** `flex: 1 1 340px`, capped at 400px — the live email preview. */
    previewBasis: "340px",
    previewMaxWidth: 400,
    /** Sticky offset = height of the app top nav. */
    previewStickyTop: 64,
  },
  /** White rounded card shared by every settings group. */
  card: {
    border: contentCard.borderMuted,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
    padding: "24px 28px",
    /** Slimmer card — the single-row "Custom Branding" switch. */
    paddingCompact: "18px 28px",
    /** Gap between header block and the control block below it. */
    gap: "14px",
  },
  /** Card heading + supporting sentence. */
  header: {
    textGap: "3px",
    titleColor: signer.ink,
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleLetterSpacing: "-0.01em",
    descriptionColor: signer.text.meta,
    descriptionFontSize: "12.5px",
    /** Branding's supporting sentences run tighter than the other screens'. */
    descriptionLineHeight: "normal",
  },
  /** Master switch row: heading is one step smaller and lighter than a card title. */
  masterSwitch: {
    gap: "14px",
    textGap: "2px",
    titleFontSize: "13.5px",
    titleFontWeight: 700,
    /** The cards below fade out while custom branding is off. */
    disabledOpacity: 0.45,
    opacityTransition: "opacity .15s",
  },
  /** Shared app toggle chrome — see `theme/colors.ts` `switchControl`. */
  toggle: {
    width: switchControl.width,
    height: switchControl.height,
    knobSize: switchControl.knobSize,
    knobTop: `${switchControl.knobInset}px`,
    knobOffsetOff: `translateX(${switchControl.knobInset}px)`,
    knobOffsetOn: `translateX(${switchControl.knobInset + switchControl.knobTravel}px)`,
    trackOff: switchControl.trackOff,
    trackOn: switchControl.trackOn,
    knobBg: switchControl.knob,
    knobShadow: switchControl.knobShadow,
    transition: switchControl.trackTransition,
    knobTransition: switchControl.knobTransition,
    /** Small padlock shown beside the toggle when the plan lacks the feature. */
    lockSize: 14,
    lockColor: signer.text.faint,
    lockGap: "6px",
  },
  /** Dashed drop target plus the hint / action links beside it. */
  logo: {
    rowGap: "18px",
    boxWidth: 150,
    boxHeight: 66,
    boxRadius: "10px",
    boxBorder: `1.5px dashed ${switchControl.trackOff}`,
    boxHoverBorderColor: signer.orange.main,
    boxBg: formField.bg,
    imageMaxWidth: 118,
    imageMaxHeight: 40,
    infoGap: "3px",
    infoFontSize: "11.5px",
    infoLineHeight: 1.6,
    infoColor: signer.text.faint,
    linkGap: "12px",
    linkFontWeight: 700,
    replaceColor: signer.orange.main,
    replaceHoverColor: signer.orange.dark,
    removeColor: notification.error.icon,
    /** The design's inline colour wins over the global link hover rule. */
    removeHoverColor: notification.error.icon,
    errorColor: notification.error.icon,
  },
  /** Two-column grid of labelled text fields. */
  field: {
    /*
      Field geometry is the app's, not this screen's: 44px on a 10px radius
      with 14px text and a 14px inset, matching Contacts and the shared
      `CustomInput` default. These four Settings screens had drifted to a
      40/9px/13px box of their own, which read as a different control from one
      page to the next. See `global/components/CustomInput/FormField.tokens.ts`.
    */
    rowGap: "14px",
    columnGap: "18px",
    labelGap: "6px",
    labelFontSize: "11.5px",
    labelFontWeight: 600,
    labelColor: signer.ink,
    requiredColor: signer.orange.main,
    height: 44,
    radius: "10px",
    border: formField.border,
    bg: formField.bg,
    color: signer.ink,
    fontSize: "14px",
    fontWeight: 500,
    /**
     * Design value is 14px measured inside a real 1px border. MUI paints the
     * outline on an overlaid `fieldset` instead of insetting it, so the input
     * needs design padding + the border width to land on the same pixel.
     */
    paddingX: "15px",
    focusBorder: signer.orange.main,
    placeholder: formField.placeholderMuted,
    errorFontSize: "10.5px",
  },
  /** Multi-line footer box — same chrome as the fields, taller and padded. */
  textarea: {
    height: 80,
    /** Design 10px/12px, plus the 1px border MUI overlays (see `field.paddingX`). */
    padding: "11px 13px",
    lineHeight: 1.6,
    /** Fixed rows keep the box exactly 80px tall (no autosizing). */
    rows: 3,
  },
  /** Preset brand colours plus the read-out chip. */
  brandColor: {
    /** Vertical gap between the two labelled colour groups. */
    groupGap: "14px",
    labelGap: "5px",
    labelFontSize: "11.5px",
    labelFontWeight: 600,
    labelColor: signer.ink,
    /** Info bubble sitting beside a group label. */
    infoGap: "5px",
    infoSize: 13,
    infoColor: signer.text.faint,
    rowGap: "10px",
    swatchSize: 34,
    swatchRadius: "9px",
    swatchRing: `2px solid ${white}`,
    swatchOutlineWidth: "2px",
    presets: [
      signer.orange.main,
      signer.ink,
      signer.purple.main,
      notification.success.icon,
      "#B92D2D",
    ],
    chipGapBefore: "8px",
    chipHeight: 40,
    chipPaddingX: "12px",
    chipRadius: "9px",
    chipBorder: formField.border,
    chipBg: formField.bg,
    chipGap: "8px",
    chipDotSize: 16,
    chipDotRadius: "4px",
    chipFontFamily: "inherit",
    chipFontSize: "12.5px",
    chipFontWeight: 600,
    chipColor: signer.ink,
    /** Blocking contrast failure — the design's "Remove" red. */
    warningColor: notification.error.icon,
    /** Non-blocking low-contrast advisory. */
    cautionColor: signer.warn.text, // WCAG 1.4.3: #B26A00 was 4.23:1 on white
    warningFontSize: "11.5px",
    warningLineHeight: 1.5,
  },
  /** Right-hand "Live Email Preview" panel. */
  preview: {
    bg: button.disabledBg,
    border: contentCard.borderMuted,
    radius: "14px",
    padding: "18px",
    gap: "12px",
    headerGap: "8px",
    titleFontSize: "12.5px",
    titleFontWeight: 800,
    titleLetterSpacing: "-0.01em",
    titleColor: signer.ink,
    badgeFontSize: "10px",
    badgeFontWeight: 700,
    badgeLetterSpacing: "0.06em",
    badgeColor: signer.purple.main,
    badgeBg: white,
    badgePadding: "3px 9px",
    badgeRadius: "999px",
    captionFontSize: "10.5px",
    captionColor: signer.text.faint,
    captionLineHeight: 1.5,
  },
  /** Mock email client card inside the preview panel. */
  email: {
    radius: "11px",
    shadow: "0 1px 4px rgba(33,21,71,.08)",
    headerPadding: "12px 16px",
    headerGap: "10px",
    headerBorder: signer.border.divider,
    avatarSize: 30,
    avatarFontSize: "11px",
    avatarFontWeight: 700,
    avatarColor: white,
    senderFontSize: "11.5px",
    senderColor: signer.ink,
    senderMutedColor: signer.text.faint,
    recipientFontSize: "10.5px",
    bodyPadding: "18px 20px",
    bodyGap: "10px",
    bodyFontSize: "11.5px",
    bodyLineHeight: 1.6,
    bodyColor: signer.text.secondary,
    strongColor: signer.ink,
    logoWidth: 86,
    ctaPadding: "9px 18px",
    ctaRadius: "8px",
    ctaFontSize: "11.5px",
    ctaFontWeight: 700,
    ctaColor: white,
    ctaMarginY: "4px",
    legalPaddingTop: "10px",
    legalFontSize: "10px",
    legalColor: signer.text.faint,
    linkColor: "#3E6AE1",
  },
  /** Chrome mirrors the global `MuiButton` `contained` override — see `button`. */
  primaryButton: {
    ...button,
    lockSize: 14,
  },
  /** Placeholder blocks shown while the settings load. */
  skeleton: {
    titleWidth: 150,
    titleHeight: 18,
    descriptionHeight: 13,
    descriptionWidth: "78%",
    labelWidth: 96,
    labelHeight: 11,
    previewHeight: 360,
  },
} as const;

/**
 * Phone and tablet values for **Settings → Branding**, traced from
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375, 390 and 834 identically — one column of cards, the
 * live preview last, then a full-width "Save Changes". Applied below `md`; the
 * desktop two-column layout is untouched.
 */
export const brandingResponsiveTokens = {
  card: { padding: "16px", gap: "12px" },
  header: { titleFontSize: "14.5px", descriptionFontSize: "12px" },
  field: { height: 44, fontSize: "13.5px" },
  textarea: { height: 84 },
  colorChip: { height: 40 },
  preview: { padding: "14px", gap: "10px" },
  primaryButton: {
    height: "44px",
    paddingX: "20px",
    radius: "9px",
    fontSize: "13px",
  },
} as const;

export default brandingTokens;
